import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import Papa from "papaparse";
import type { CsvRow, DataProvider, PlayerComboEntry } from "../types/draft";
import { validateHeaders, validateRows, summarizeSkippedRows } from "../helpers/csvHelpers";
import { REQUIRED_CSV_HEADERS } from "../constants";
import { groupDataByPlayer, buildPlayersByDraftIndex, getPlayerCombos } from "../helpers/playerHelpers";
import { resolveIdentities } from "../helpers/playerIdentity";
import { getTeamsTableFromCsv } from "../helpers/chartHelpers";
import { groupTournamentsByType, type TournamentGroups } from "../helpers/tournamentHelpers";
import { analyzePortfolioStacks } from "../services/stackDetection";
import { analyzePortfolioArchetypes } from "../services/archetypeClassifier";
import type { PortfolioStackAnalysis } from "../services/stackDetection";
import type { PortfolioArchetypeAnalysis } from "../services/archetypeClassifier";

const STORAGE_KEY = "rosterOS_rawData";
// Stay conservatively below the 5MB localStorage limit
const MAX_PERSIST_BYTES = 4 * 1024 * 1024;

// Minimum fraction of picks that must carry a Team abbreviation before we treat
// team-dependent features (stacks, archetypes, team filter) as meaningful.
// Underdog imports are ~1.0; a DraftKings import whose exposure/team fetch failed
// is ~0, and we degrade those surfaces gracefully rather than showing blanks.
const TEAM_COVERAGE_THRESHOLD = 0.5;

function loadFromStorage(): CsvRow[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CsvRow[]) : [];
  } catch {
    return [];
  }
}

function saveToStorage(data: CsvRow[]): void {
  try {
    if (data.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    const serialized = JSON.stringify(data);
    if (serialized.length <= MAX_PERSIST_BYTES) {
      localStorage.setItem(STORAGE_KEY, serialized);
    } else {
      // Two full portfolios (UD + DK) can exceed the ~4MB budget. The merged
      // session still works in memory; we just can't persist it across reloads.
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Quota exceeded or private-browsing restriction — silently skip
  }
}

// ── Provider merge helpers ────────────────────────────────────────
// Both Underdog and DraftKings imports carry a per-row `Provider` stamp, so a
// single rawData array can hold both platforms at once. Re-importing a platform
// REPLACES that platform's slice (a refresh), leaving the other platform intact.

/** Normalized provider label for a row; legacy CSVs with no stamp → "Unknown". */
export function rowProvider(row: CsvRow): string {
  return (row["Provider"] || "").trim() || "Unknown";
}

/** Natural key for a pick, unique within a platform's slice. */
function rowKey(row: CsvRow): string {
  return `${rowProvider(row)}|${row["Draft"]}|${row["Pick Number"]}`;
}

function providersIn(rows: CsvRow[]): Set<string> {
  const s = new Set<string>();
  for (const r of rows) s.add(rowProvider(r));
  return s;
}

/**
 * Merge an incoming import into the existing portfolio. Any provider present in
 * `incoming` fully replaces its prior slice (so a fresh sync of one platform
 * refreshes only that platform); other providers are preserved. Picks are then
 * de-duplicated by (provider, draft, pick number).
 */
export function mergeRows(existing: CsvRow[], incoming: CsvRow[]): CsvRow[] {
  const incomingProviders = providersIn(incoming);
  const kept = existing.filter(r => !incomingProviders.has(rowProvider(r)));
  const seen = new Set<string>();
  const out: CsvRow[] = [];
  for (const r of kept.concat(incoming)) {
    const k = rowKey(r);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(r);
  }
  return out;
}

// ── Types ────────────────────────────────────────────────────────

export interface ComboCacheEntry {
  top2: PlayerComboEntry[];
  top3: PlayerComboEntry[];
}

// ── Context shape ─────────────────────────────────────────────────

interface DraftDataContextValue {
  rawData: CsvRow[];
  filteredRawData: CsvRow[];
  tournamentOptions: string[];
  tournamentGroups: TournamentGroups;
  tournamentFilter: string[];
  teamOptions: string[];
  teamFilter: string;
  positionOptions: string[];
  positionFilter: string;
  /** Platform filter for every page: "all" | a provider label. Default "all". */
  providerFilter: string;
  /** Distinct providers loaded, in a stable order (e.g. ["Underdog","DraftKings"]). */
  providersPresent: string[];
  /** Draft counts per provider, keyed by provider label. */
  providerDraftCounts: Record<string, number>;
  /** True when more than one platform is loaded (drives the provider toggle). */
  hasMultipleProviders: boolean;
  fileName: string;
  error: string;
  skipSummary: string;
  isParsing: boolean;
  playerCount: number;
  teamCount: number;
  uploadedAt: Date | null;
  /** Import source, derived from the rows' Provider column. */
  provider: DataProvider;
  /** Fraction (0–1) of loaded picks that carry a Team abbreviation. */
  teamCoverage: number;
  /** True when team data is present enough for stacks/team analysis to be shown. */
  hasTeamData: boolean;
  setTournamentFilter: (v: string[]) => void;
  setTeamFilter: (v: string) => void;
  setPositionFilter: (v: string) => void;
  setProviderFilter: (v: string) => void;
  setSkipSummary: (v: string) => void;
  handleFile: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Import mode for handleFile: "merge" refreshes only the imported platform's
   *  slice (default); "replace" clears the whole portfolio first. */
  importMode: "merge" | "replace";
  setImportMode: (v: "merge" | "replace") => void;
  clearData: () => void;
  /** Remove a single platform's slice, leaving the other(s) intact. */
  clearProvider: (provider: string) => void;
  combosCache: Map<string, ComboCacheEntry>;
  isProcessingCombos: boolean;
  /** Portfolio-level stack analysis (Phase 1a). Null when no data is loaded. */
  portfolioStacks: PortfolioStackAnalysis | null;
  /** Portfolio-level archetype analysis (Phase 1c). Null when no data is loaded. */
  portfolioArchetypes: PortfolioArchetypeAnalysis | null;
}

const DraftDataContext = createContext<DraftDataContextValue | undefined>(undefined);

// ── Provider ──────────────────────────────────────────────────────

export function DraftDataProvider({ children }: { children: React.ReactNode }) {
  const [rawData, setRawData] = useState<CsvRow[]>(loadFromStorage);
  const [error, setError] = useState("");
  const [fileName, setFileName] = useState(() => {
    // If we restored data from storage, show a generic filename indicator
    const stored = loadFromStorage();
    return stored.length > 0 ? "(restored from last session)" : "";
  });
  const [skipSummary, setSkipSummary] = useState("");
  const [isParsing, setIsParsing] = useState(false);
  const [uploadedAt, setUploadedAt] = useState<Date | null>(null);
  const [tournamentFilter, setTournamentFilter] = useState<string[]>([]);
  const [teamFilter, setTeamFilter] = useState("");
  const [positionFilter, setPositionFilter] = useState("");
  const [providerFilter, setProviderFilter] = useState("all");
  const [importMode, setImportMode] = useState<"merge" | "replace">("merge");
  const [combosCache, setCombosCache] = useState<Map<string, ComboCacheEntry>>(new Map());
  const [isProcessingCombos, setIsProcessingCombos] = useState(false);

  // Persist rawData whenever it changes
  useEffect(() => {
    saveToStorage(rawData);
  }, [rawData]);

  // Listen for data injected by the rosterOS Chrome extension via localStorage.
  // The extension bundles all synced platforms into one array, but we merge (not
  // replace) so a second injection accumulates instead of wiping the first.
  useEffect(() => {
    function handleStorage(e: StorageEvent) {
      if (e.key !== STORAGE_KEY || !e.newValue) return;
      try {
        const rows = JSON.parse(e.newValue) as CsvRow[];
        if (Array.isArray(rows) && rows.length > 0) {
          setRawData(prev => mergeRows(prev, rows));
          setFileName('(imported from rosterOS extension)');
          setUploadedAt(new Date());
          setError('');
          setSkipSummary('');
        }
      } catch {
        // Ignore malformed data
      }
    }
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // ── Cross-platform identity resolution ───────────────────────────
  // Underdog and DraftKings name the same player differently ("Kenny Gainwell"
  // vs "Kenneth Gainwell", "Marvin Harrison" vs "Marvin Harrison Jr.") and
  // disagree on stale teams/positions. Reconciling ONCE here — upstream of every
  // filter, grouping, chart and analysis — is what stops one human showing up as
  // two rows in My Players. `rawData` itself stays untouched so the persisted
  // copy and the per-platform slices in `mergeRows` keep their original values.
  const resolvedData = useMemo(() => resolveIdentities(rawData), [rawData]);

  const tournamentOptions = useMemo(
    () => Array.from(new Set(resolvedData.map(row => row["Tournament Title"]).filter(Boolean))),
    [resolvedData]
  );

  // Pre-draft vs regular-season grouping (by draft date). See tournamentHelpers.
  const tournamentGroups = useMemo(() => groupTournamentsByType(resolvedData), [resolvedData]);

  const teamOptions = useMemo(
    () => Array.from(new Set(resolvedData.map(row => row["Team"]).filter(Boolean))).sort(),
    [resolvedData]
  );

  const positionOptions = useMemo(
    () => Array.from(new Set(resolvedData.map(row => row["Position"]).filter(Boolean))).sort(),
    [resolvedData]
  );

  // Auto-select all tournaments when data first loads.
  useEffect(() => {
    if (tournamentOptions.length > 0 && tournamentFilter.length === 0) {
      setTournamentFilter(tournamentOptions);
    }
  }, [tournamentOptions, tournamentFilter.length]);

  // Base filter chain: provider (platform) first, then tournament. Applying the
  // provider filter here means every downstream surface (combos, exposures,
  // stacks, archetypes, KPIs) respects "UD only / DK only / both" for free.
  const tournamentFilteredData = useMemo(() => {
    const tournActive = tournamentFilter.length > 0;
    if (providerFilter === "all" && !tournActive) return resolvedData;
    return resolvedData.filter(row => {
      if (providerFilter !== "all" && rowProvider(row) !== providerFilter) return false;
      if (tournActive && !tournamentFilter.includes(row["Tournament Title"] ?? "")) return false;
      return true;
    });
  }, [resolvedData, providerFilter, tournamentFilter]);

  // Pre-compute all player combos eagerly after data loads so row expansion is instant.
  // Depends on tournamentFilteredData so combo counts and player draft counts share the same base.
  // Processes one player per setTimeout tick to avoid freezing the main thread.
  useEffect(() => {
    if (tournamentFilteredData.length === 0) {
      setCombosCache(new Map());
      setIsProcessingCombos(false);
      return;
    }
    let cancelled = false;
    setIsProcessingCombos(true);
    setCombosCache(new Map());
    const allPlayers = groupDataByPlayer(tournamentFilteredData);
    const playersByDraftIndex = buildPlayersByDraftIndex(allPlayers);
    const cache = new Map<string, ComboCacheEntry>();
    let idx = 0;
    const BATCH_SIZE = 10;
    function processNext() {
      if (cancelled) return;
      const end = Math.min(idx + BATCH_SIZE, allPlayers.length);
      for (; idx < end; idx++) {
        const player = allPlayers[idx];
        const key = `${player.name}|${player.team}|${player.position}`;
        cache.set(key, {
          top2: getPlayerCombos(player, allPlayers, 2, 5, playersByDraftIndex),
          top3: getPlayerCombos(player, allPlayers, 3, 5, playersByDraftIndex),
        });
      }
      if (idx >= allPlayers.length) {
        setCombosCache(new Map(cache));
        setIsProcessingCombos(false);
        return;
      }
      setTimeout(processNext, 0);
    }
    setTimeout(processNext, 0);
    return () => { cancelled = true; };
  }, [tournamentFilteredData]);

  const filteredRawData = useMemo(() => {
    return tournamentFilteredData.filter(row => {
      if (positionFilter && row["Position"] !== positionFilter) return false;
      return true;
    });
  }, [tournamentFilteredData, positionFilter]);

  const playerCount = useMemo(
    () => new Set(filteredRawData.map(row =>
      `${(row["First Name"] || "").trim()} ${(row["Last Name"] || "").trim()}|${(row["Team"] || "").trim()}|${(row["Position"] || "").trim()}`
    )).size,
    [filteredRawData]
  );

  const teamCount = useMemo(
    () => new Set(filteredRawData.map(row => row["Draft"])).size,
    [filteredRawData]
  );

  // ── Provider + team-data coverage ────────────────────────────────
  // Derived once here so every page shares one source of truth for the source
  // label and the "is stacking meaningful?" decision.
  const provider = useMemo<DataProvider>(() => {
    const counts: Record<string, number> = {};
    for (const row of resolvedData) {
      const p = (row["Provider"] || "").trim();
      if (p) counts[p] = (counts[p] || 0) + 1;
    }
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0];
    if (top === "DraftKings") return "DraftKings";
    if (top === "Underdog") return "Underdog";
    return "Unknown";
  }, [resolvedData]);

  // Which platforms are loaded + how many drafts each contributes. Order is
  // stable (Underdog, DraftKings, then any legacy "Unknown") for consistent UI.
  const PROVIDER_ORDER = ["Underdog", "DraftKings", "Unknown"];
  const { providersPresent, providerDraftCounts } = useMemo(() => {
    const draftsByProvider: Record<string, Set<string>> = {};
    for (const row of resolvedData) {
      const p = rowProvider(row);
      (draftsByProvider[p] ??= new Set()).add(row["Draft"]);
    }
    const counts: Record<string, number> = {};
    for (const p of Object.keys(draftsByProvider)) counts[p] = draftsByProvider[p].size;
    const present = Object.keys(draftsByProvider).sort(
      (a, b) => (PROVIDER_ORDER.indexOf(a) + 1 || 99) - (PROVIDER_ORDER.indexOf(b) + 1 || 99)
    );
    return { providersPresent: present, providerDraftCounts: counts };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedData]);

  const hasMultipleProviders = providersPresent.length > 1;

  // If the active provider filter points at a platform that's no longer loaded
  // (e.g. after removing a source), fall back to "all".
  useEffect(() => {
    if (providerFilter !== "all" && !providersPresent.includes(providerFilter)) {
      setProviderFilter("all");
    }
  }, [providerFilter, providersPresent]);

  const teamCoverage = useMemo(() => {
    if (filteredRawData.length === 0) return 1;
    const withTeam = filteredRawData.reduce(
      (n, row) => ((row["Team"] || "").trim() ? n + 1 : n),
      0
    );
    return withTeam / filteredRawData.length;
  }, [filteredRawData]);

  const hasTeamData = teamCoverage >= TEAM_COVERAGE_THRESHOLD;

  // ── Phase 1a: Stack detection ────────────────────────────────────
  // Short-circuit to null when team data is missing (e.g. a DraftKings import
  // whose team fetch failed): stacks are meaningless without teams, and the UI
  // renders a "team data unavailable" note instead of empty results.
  const portfolioStacks = useMemo<PortfolioStackAnalysis | null>(() => {
    if (filteredRawData.length === 0 || !hasTeamData) return null;
    const teams = getTeamsTableFromCsv(filteredRawData);
    return analyzePortfolioStacks(teams);
  }, [filteredRawData, hasTeamData]);

  // ── Phase 1c: Archetype classification ───────────────────────────
  // NOT gated on team data: archetypes are derived from position + pick round
  // (stars-and-scrubs, balanced, hero-RB, …), which are present regardless of
  // whether the import carries NFL team abbreviations.
  const portfolioArchetypes = useMemo<PortfolioArchetypeAnalysis | null>(() => {
    if (filteredRawData.length === 0) return null;
    const teams = getTeamsTableFromCsv(filteredRawData);
    return analyzePortfolioArchetypes(teams);
  }, [filteredRawData]);

  const handleFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setError("");
    setSkipSummary("");
    const file = e.target.files?.[0];
    setFileName(file ? file.name : "");
    if (!file) return;

    // Reject files over 10 MB.
    if (file.size > 10 * 1024 * 1024) {
      setError("File is too large. Please upload a CSV smaller than 10 MB.");
      return;
    }

    setIsParsing(true);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: result => {
        setIsParsing(false);
        try {
          if (!result.data || !result.data.length) {
            setError("No data found in CSV.");
            return;
          }
          const missing = validateHeaders(result.data[0] as Record<string, unknown>, REQUIRED_CSV_HEADERS);
          if (missing.length) {
            setError(`Missing CSV headers: ${missing.join(", ")}`);
            return;
          }
          const { validRows, skippedRows } = validateRows(result.data as Record<string, string>[]);
          const summary = summarizeSkippedRows(skippedRows);
          if (summary) setSkipSummary(summary);
          setError("");
          setUploadedAt(new Date());
          // "merge" refreshes only the imported platform's slice and keeps the
          // other platform; "replace" starts fresh from this file alone.
          setRawData(prev =>
            importMode === "replace"
              ? (validRows as CsvRow[])
              : mergeRows(prev, validRows as CsvRow[])
          );
        } catch {
          setIsParsing(false);
          setError("Unexpected error while parsing CSV. Please check the file and try again.");
        }
      },
      error: () => {
        setIsParsing(false);
        setError("Failed to read the file. Please try again.");
      },
    });
    // Allow re-selecting the same file (some browsers skip the change event).
    e.target.value = "";
  }, [importMode]);

  const clearProvider = useCallback((providerToRemove: string) => {
    setRawData(prev => prev.filter(r => rowProvider(r) !== providerToRemove));
  }, []);

  const clearData = useCallback(() => {
    setRawData([]);
    setFileName("");
    setError("");
    setSkipSummary("");
    setUploadedAt(null);
    setTournamentFilter([]);
    setTeamFilter("");
    setPositionFilter("");
    setProviderFilter("all");
  }, []);

  const contextValue = useMemo(() => ({
    // Consumers always see the identity-resolved view. The unreconciled array
    // stays private to this provider — it is what gets persisted and what
    // `mergeRows` slices by platform, so re-importing one platform still
    // replaces exactly that platform's original rows.
    rawData: resolvedData,
    filteredRawData,
    tournamentOptions,
    tournamentGroups,
    tournamentFilter,
    teamOptions,
    teamFilter,
    positionOptions,
    positionFilter,
    providerFilter,
    providersPresent,
    providerDraftCounts,
    hasMultipleProviders,
    fileName,
    error,
    skipSummary,
    isParsing,
    playerCount,
    teamCount,
    uploadedAt,
    provider,
    teamCoverage,
    hasTeamData,
    setTournamentFilter,
    setTeamFilter,
    setPositionFilter,
    setProviderFilter,
    setSkipSummary,
    handleFile,
    importMode,
    setImportMode,
    clearData,
    clearProvider,
    combosCache,
    isProcessingCombos,
    portfolioStacks,
    portfolioArchetypes,
  }), [
    resolvedData, filteredRawData, tournamentOptions, tournamentGroups, tournamentFilter,
    teamOptions, teamFilter, positionOptions, positionFilter,
    providerFilter, providersPresent, providerDraftCounts, hasMultipleProviders,
    fileName, error, skipSummary, isParsing, playerCount, teamCount,
    uploadedAt, provider, teamCoverage, hasTeamData,
    setTournamentFilter, setTeamFilter, setPositionFilter, setProviderFilter,
    setSkipSummary, handleFile, importMode, setImportMode, clearData, clearProvider,
    combosCache, isProcessingCombos,
    portfolioStacks, portfolioArchetypes,
  ]);

  return (
    <DraftDataContext.Provider value={contextValue}>
      {children}
    </DraftDataContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────

export function useDraftData(): DraftDataContextValue {
  const ctx = useContext(DraftDataContext);
  if (!ctx) throw new Error("useDraftData must be used within DraftDataProvider");
  return ctx;
}
